import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import WidgetRenderer from './WidgetRenderer';

const CanvasWorkspace = ({ widgets, onWidgetAdd, onWidgetUpdate, onWidgetDelete, onWidgetSelect, selectedWidget }) => {
  const [dragOver, setDragOver] = useState(false);
  const [dropIndicator, setDropIndicator] = useState(null);
  const canvasRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);

    // Calculate drop position for visual feedback
    const rect = canvasRef?.current?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const y = e?.clientY - rect?.top;

    // Snap to grid (20px grid)
    const snappedX = Math.round(x / 20) * 20;
    const snappedY = Math.round(y / 20) * 20;

    setDropIndicator({ x: snappedX, y: snappedY });
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!canvasRef?.current?.contains(e?.relatedTarget)) {
      setDragOver(false);
      setDropIndicator(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setDragOver(false);
    setDropIndicator(null);

    try {
      const widgetData = JSON.parse(e?.dataTransfer?.getData('application/json'));
      const rect = canvasRef?.current?.getBoundingClientRect();
      const x = e?.clientX - rect?.left;
      const y = e?.clientY - rect?.top;

      // Snap to grid
      const snappedX = Math.round(x / 20) * 20;
      const snappedY = Math.round(y / 20) * 20;

      const newWidget = {
        id: `widget-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
        type: widgetData?.id,
        name: widgetData?.name,
        position: { x: snappedX, y: snappedY },
        size: { width: 300, height: 200 },
        content: getDefaultContent(widgetData?.id),
        style: {
          backgroundColor: '#ffffff',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          padding: 16
        }
      };

      onWidgetAdd(newWidget);
    } catch (error) {
      console.error('Error parsing dropped widget data:', error);
    }
  }, [onWidgetAdd]);

  const getDefaultContent = (widgetType) => {
    const defaultContents = {
      'author-bio': {
        name: 'Author Name',
        bio: 'Enter author biography here...',
        image: "https://images.unsplash.com/photo-1498200705497-2c9e717e5ab8",
        imageAlt: 'Professional headshot of author with warm smile in business attire'
      },
      'author-headshot': {
        image: "https://images.unsplash.com/photo-1609170665173-d79926c07fa8",
        imageAlt: 'Professional portrait of female author with confident expression in modern setting',
        caption: 'Professional Author Photo'
      },
      'book-synopsis': {
        title: 'Book Title',
        synopsis: 'Enter your compelling book synopsis here...',
        genre: 'Fiction',
        pages: '320',
        isbn: '978-0-123456-78-9'
      },
      'book-cover': {
        image: "https://images.unsplash.com/photo-1644060473538-dbbe07fd5195",
        imageAlt: 'Book cover featuring elegant typography and artistic design elements',
        title: 'Book Title',
        author: 'Author Name'
      },
      'video-player': {
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        title: 'Author Interview',
        description: 'Watch the exclusive author interview'
      },
      'download-section': {
        title: 'Press Materials',
        files: [
        { name: 'High-Res Book Cover', type: 'JPG', size: '2.4 MB' },
        { name: 'Author Bio & Photo', type: 'PDF', size: '1.8 MB' },
        { name: 'Sample Chapters', type: 'PDF', size: '3.2 MB' }]

      },
      'company-info': {
        name: 'PenGame Publishing',
        description: 'Leading independent publisher specializing in contemporary fiction and non-fiction.',
        founded: '2015',
        location: 'New York, NY',
        website: 'www.pengamepublishing.com'
      }
    };

    return defaultContents?.[widgetType] || { text: 'Widget content' };
  };

  const handleWidgetClick = (widget, e) => {
    e?.stopPropagation();
    onWidgetSelect(widget);
  };

  const handleCanvasClick = () => {
    onWidgetSelect(null);
  };

  return (
    <div className="flex-1 bg-muted/30 relative overflow-hidden">
      {/* Canvas Header */}
      <div className="absolute top-0 left-0 right-0 bg-card border-b border-border p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Layout" size={20} />
              <span>Canvas Workspace</span>
            </h2>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Grid3x3" size={16} />
              <span>Grid: 20px</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors duration-200">
              <Icon name="ZoomIn" size={16} />
            </button>
            <button className="p-2 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors duration-200">
              <Icon name="ZoomOut" size={16} />
            </button>
            <button className="p-2 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors duration-200">
              <Icon name="RotateCcw" size={16} />
            </button>
          </div>
        </div>
      </div>
      {/* Canvas Area */}
      <div
        ref={canvasRef}
        className="absolute inset-0 top-20 overflow-auto"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleCanvasClick}
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }}>

        {/* Drop Indicator */}
        {dragOver && dropIndicator &&
        <div
          className="absolute border-2 border-dashed border-primary bg-primary/10 rounded-lg pointer-events-none z-20"
          style={{
            left: dropIndicator?.x,
            top: dropIndicator?.y,
            width: 300,
            height: 200
          }}>

            <div className="flex items-center justify-center h-full">
              <div className="text-primary font-medium">Drop widget here</div>
            </div>
          </div>
        }

        {/* Drag Over Overlay */}
        {dragOver &&
        <div className="absolute inset-0 bg-primary/5 border-2 border-dashed border-primary rounded-lg pointer-events-none z-10">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Icon name="Plus" size={48} className="text-primary mx-auto mb-2" />
                <p className="text-lg font-medium text-primary">Drop widget to add</p>
              </div>
            </div>
          </div>
        }

        {/* Canvas Content */}
        <div className="relative min-h-full min-w-full p-8">
          {widgets?.length === 0 && !dragOver &&
          <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="MousePointer2" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-medium text-foreground mb-2">Start Building Your Press Kit</h3>
                <p className="text-muted-foreground max-w-md">
                  Drag widgets from the library on the left to create your professional press kit. 
                  Arrange them however you like with our grid-based layout system.
                </p>
              </div>
            </div>
          }

          {/* Render Widgets */}
          {widgets?.map((widget) =>
          <div
            key={widget?.id}
            className={`absolute cursor-pointer transition-all duration-200 ${
            selectedWidget?.id === widget?.id ?
            'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-1 hover:ring-border'}`
            }
            style={{
              left: widget?.position?.x,
              top: widget?.position?.y,
              width: widget?.size?.width,
              height: widget?.size?.height
            }}
            onClick={(e) => handleWidgetClick(widget, e)}>

              <WidgetRenderer
              widget={widget}
              onUpdate={onWidgetUpdate}
              onDelete={onWidgetDelete}
              isSelected={selectedWidget?.id === widget?.id} />

            </div>
          )}
        </div>
      </div>
    </div>);

};

export default CanvasWorkspace;